import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Copy, RefreshCcw, Trash2, ExternalLink, Apple } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Calendar subscription card — lets a player subscribe their phone/desktop
 * calendar to a personal feed of every event they've joined or organize on
 * Pick2Play. The calendar app polls the URL automatically so new joins show
 * up without any further action by the user.
 *
 * UX rules:
 *  - Token is generated lazily on first click ("Set up my calendar") so we
 *    don't issue one for users who never use this feature.
 *  - Once issued, we show the URL with copy + a one-click "Add to Apple
 *    Calendar" via the webcal:// scheme (iOS auto-prompts).
 *  - Rotate / revoke buttons are clearly destructive (orange / red).
 */
export default function CalendarSubscriptionCard() {
  const [data, setData] = useState(null);   // { token, https_url, webcal_url } | null
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // We don't auto-load on mount — we want the user to opt in. But if the user
  // already has a token from a previous visit, surface it on first interaction.
  const ensureToken = async (rotate = false) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/users/me/calendar-token${rotate ? "?rotate=true" : ""}`);
      setData(r);
      setLoaded(true);
      if (rotate) toast.success("Calendar URL rotated. Re-subscribe with the new link.");
      return r;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't issue calendar token");
    } finally { setBusy(false); }
  };

  // Lightly check if the user already has one (so the existing URL surfaces immediately)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: r } = await api.post("/users/me/calendar-token");
        if (mounted) { setData(r); setLoaded(true); }
      } catch { /* ignore — user can click the button */ }
    })();
    return () => { mounted = false; };
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — please long-press to select");
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke calendar access? Subscribed calendars will stop syncing immediately.")) return;
    setBusy(true);
    try {
      await api.delete("/users/me/calendar-token");
      setData(null);
      toast.success("Calendar token revoked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Revoke failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="pp-card-elevated p-6 space-y-3" data-testid="settings-calendar-subscribe">
      <div className="flex items-center gap-2">
        <CalendarPlus size={18} className="text-[#00E676]" />
        <h3 className="font-heading text-xl font-bold">Subscribe my phone calendar</h3>
      </div>
      <p className="text-sm text-zinc-400">
        One-time setup: paste this link into Apple Calendar / Google Calendar / Outlook and every match,
        tournament, league or lesson you join (or organize) will appear automatically. No more "Add to
        calendar" on each event.
      </p>

      {!loaded && (
        <button
          onClick={() => ensureToken(false)}
          disabled={busy}
          data-testid="settings-cal-setup"
          className="pp-btn-primary px-5 py-2 rounded-lg text-sm disabled:opacity-50"
        >
          {busy ? "Setting up…" : "Set up my calendar"}
        </button>
      )}

      {loaded && data && (
        <div className="space-y-4 pt-2">
          {/* HTTPS URL — universal, works in Google Calendar, Outlook desktop, etc. */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Subscription URL</div>
            <div className="flex gap-2 items-stretch">
              <input
                type="text"
                readOnly
                value={data.https_url}
                onClick={(e) => e.target.select()}
                data-testid="settings-cal-url"
                className="pp-input flex-1 text-xs font-mono"
              />
              <button
                onClick={() => copy(data.https_url, "Subscription URL")}
                data-testid="settings-cal-copy"
                title="Copy URL"
                className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>

          {/* Quick-launch buttons */}
          <div className="flex flex-wrap gap-2">
            <a
              href={data.webcal_url}
              data-testid="settings-cal-apple"
              className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"
              title="Opens Apple Calendar's Add Subscription dialog"
            >
              <Apple size={13} /> Apple / iOS Calendar
            </a>
            <a
              href={`https://calendar.google.com/calendar/u/0/r/settings/addbyurl?cid=${encodeURIComponent(data.https_url)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="settings-cal-google"
              className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"
              title="Opens Google Calendar's Add by URL screen"
            >
              <span className="inline-block h-3 w-3 rounded-full bg-gradient-to-br from-[#4285F4] via-[#EA4335] to-[#FBBC04]" />
              Google Calendar <ExternalLink size={11} />
            </a>
            <a
              href="https://outlook.live.com/owa/?path=/options/calendar/SharedCalendars"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="settings-cal-outlook"
              className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1.5"
              title="In Outlook, paste the URL into 'Subscribe from web'"
            >
              <span className="inline-block h-3 w-3 rounded-sm bg-[#0078D4]" />
              Outlook (paste URL) <ExternalLink size={11} />
            </a>
          </div>

          {/* Step-by-step copy — Apple instructions are the most useful since iOS users tap webcal directly */}
          <details className="pt-2">
            <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300">
              How to add this to my calendar app
            </summary>
            <ol className="text-xs text-zinc-400 mt-2 space-y-1.5 list-decimal ms-4">
              <li><b>iPhone / iPad / Mac:</b> tap "Apple / iOS Calendar" above — it opens the Add Subscription dialog automatically.</li>
              <li><b>Google Calendar:</b> tap "Google Calendar" above (signs you in if needed) and confirm "Add calendar".</li>
              <li><b>Outlook:</b> open <i>Calendar → Add calendar → Subscribe from web</i> and paste the URL.</li>
              <li>Calendars sync about every hour — new joins show up automatically.</li>
            </ol>
          </details>

          <div className="flex gap-2 pt-2 border-t border-white/5">
            <button
              onClick={() => ensureToken(true)}
              disabled={busy}
              data-testid="settings-cal-rotate"
              title="Issue a new URL — old one stops working immediately"
              className="px-3 py-1.5 rounded-lg text-xs border border-[#FFD60A]/30 text-[#FFD60A] hover:bg-[#FFD60A]/10 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCcw size={11} /> Rotate URL
            </button>
            <button
              onClick={revoke}
              disabled={busy}
              data-testid="settings-cal-revoke"
              title="Stop syncing immediately"
              className="px-3 py-1.5 rounded-lg text-xs border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1 disabled:opacity-50"
            >
              <Trash2 size={11} /> Revoke
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
